import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import Button from '../components/ui/button'
import { useLanguage } from '../context/LanguageContext'
import { useTenant } from '../context/TenantContext'
import apiService from '../services/api.service'
import logger from '../utils/logger'
import { sanitizeLogMessage } from '../utils/sanitize'

const parseConfig = (value) => {
  if (!value) return {}
  if (typeof value === 'object') return value

  try {
    return JSON.parse(value)
  } catch (err) {
    logger.error('Failed to parse email unsubscribe tenant config:', sanitizeLogMessage(err))
    return {}
  }
}

const buildTenantLogoUrl = (activeTenant) => {
  const logo = activeTenant?.tenantlogo
  if (!logo) return null
  if (/^https?:\/\//i.test(logo)) return logo

  const cloudfrontBase = process.env.REACT_APP_CLOUDFRONT || ''
  return `${cloudfrontBase}${logo}`
}

const EmailUnsubscribe = () => {
  const { euid, distributorid } = useParams()
  const [searchParams] = useSearchParams()
  const { t } = useLanguage()
  const { tenant } = useTenant()
  const [status, setStatus] = useState('idle')
  const [error, setError] = useState('')
  const [unsubscribeTenant, setUnsubscribeTenant] = useState(null)

  const token = searchParams.get('token')
  const hasToken = Boolean(token)
  const isSubmitting = status === 'submitting'
  const isValidating = status === 'validating'
  const isSuccess = status === 'success'
  const isInvalid = status === 'invalid'
  const hasLegacyParams = Boolean(euid && distributorid)
  const hasRequiredParams = hasToken || hasLegacyParams
  const activeTenant = unsubscribeTenant || tenant
  const masterConfig = parseConfig(activeTenant?.tenantmasterconfig)
  const themeConfig = masterConfig?.theme || {}
  const unsubscribeConfig = masterConfig?.email_unsubscribe || {}
  const showLogo = unsubscribeConfig.show_logo !== false
  const logoUrl = showLogo ? buildTenantLogoUrl(activeTenant) : null
  const primaryColor = themeConfig.primaryColor || '#001F5C'
  const primaryForeground = themeConfig.primaryForeground || '#ffffff'
  const privacyPolicyUrl = t('emailUnsubscribe.privacyPolicyUrl')
  const privacyPolicyLabel = t('emailUnsubscribe.privacyPolicyLabel')

  useEffect(() => {
    let cancelled = false

    const validateToken = async () => {
      if (!hasToken) {
        if (!hasLegacyParams) {
          setError(t('emailUnsubscribe.invalidLink'))
          setStatus('invalid')
        }
        return
      }

      setStatus('validating')
      setError('')

      try {
        const response = await apiService.post(
          '/api/cards/emailunsubscription/validate',
          { token },
        )
        if (cancelled) return

        if (response?.tenantContext) {
          setUnsubscribeTenant(response.tenantContext)
        }

        if (response?.valid === false) {
          setError(
            response?.expired
              ? t('emailUnsubscribe.expiredLink')
              : response?.message || t('emailUnsubscribe.invalidLink'),
          )
          setStatus('invalid')
          return
        }

        setStatus('idle')
      } catch (err) {
        if (cancelled) return
        logger.error('Email unsubscribe token validation failed:', sanitizeLogMessage(err))
        if (err?.response?.data?.tenantContext) {
          setUnsubscribeTenant(err.response.data.tenantContext)
        }
        setError(t('emailUnsubscribe.invalidLink'))
        setStatus('invalid')
      }
    }

    validateToken()

    return () => {
      cancelled = true
    }
  }, [hasLegacyParams, hasToken, t, token])

  const handleUnsubscribe = async () => {
    if (isSubmitting || isValidating || isSuccess || isInvalid) return

    if (!hasRequiredParams) {
      setError(t('emailUnsubscribe.invalidLink'))
      setStatus('invalid')
      return
    }

    setStatus('submitting')
    setError('')

    try {
      const payload = hasToken
        ? { token }
        : {
            externaluserid: euid,
            distributorid,
            tenantid: tenant?.tenantid,
          }
      await apiService.put('/api/cards/emailunsubscription', payload)
      setStatus('success')
    } catch (err) {
      logger.error('Email unsubscribe failed:', sanitizeLogMessage(err))
      setError(t('emailUnsubscribe.errorMessage'))
      setStatus('error')
    }
  }

  const renderFooterNote = () => (
    <p className="mt-16 max-w-4xl text-left text-sm leading-7" style={{ color: primaryColor }}>
      {t('emailUnsubscribe.privacyNotePrefix') !== 'emailUnsubscribe.privacyNotePrefix' ? (
        <>
          {t('emailUnsubscribe.privacyNotePrefix')}{' '}
          {privacyPolicyUrl && privacyPolicyUrl !== 'emailUnsubscribe.privacyPolicyUrl' ? (
            <a
              href={privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              {privacyPolicyLabel}
            </a>
          ) : (
            privacyPolicyLabel
          )}
          {t('emailUnsubscribe.privacyNoteSuffix')}
        </>
      ) : (
        t('emailUnsubscribe.successNote')
      )}
    </p>
  )

  return (
    <main className="min-h-screen bg-white px-8 py-8 text-left sm:px-12">
      <section className="mx-auto w-full max-w-5xl">
        {showLogo && (
          logoUrl ? (
            <img
              src={logoUrl}
              alt={activeTenant?.tenantname || 'Logo'}
              className="h-14 w-auto object-contain"
            />
          ) : activeTenant?.tenantname ? (
            <p className="text-lg font-semibold" style={{ color: primaryColor }}>
              {activeTenant.tenantname}
            </p>
          ) : null
        )}

        {isSuccess ? (
          <div className="mt-20 text-left">
            <CheckCircle2 className="h-11 w-11 text-green-600" aria-hidden="true" />
            <h1 className="mt-10 text-4xl font-semibold tracking-tight" style={{ color: primaryColor }}>
              {t('emailUnsubscribe.successTitle')}
            </h1>
            <p className="mt-6 max-w-3xl text-sm leading-7" style={{ color: primaryColor }}>
              {t('emailUnsubscribe.successMessage')}
            </p>
            {renderFooterNote()}
          </div>
        ) : isInvalid ? (
          <div className="mt-20 text-left">
            <AlertCircle className="h-11 w-11 text-red-600" aria-hidden="true" />
            <h1 className="mt-10 text-4xl font-semibold tracking-tight" style={{ color: primaryColor }}>
              {t('emailUnsubscribe.invalidTitle')}
            </h1>
            <p className="mt-6 max-w-3xl text-sm leading-7" style={{ color: primaryColor }}>
              {error || t('emailUnsubscribe.invalidLink')}
            </p>
          </div>
        ) : (
          <div className="mt-20 text-left">
            <p className="max-w-3xl text-sm leading-7" style={{ color: primaryColor }}>
              {t('emailUnsubscribe.confirmationMessage')}
            </p>

            {error && (
              <div className="mt-5 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-left text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="button"
              onClick={handleUnsubscribe}
              disabled={isSubmitting || isValidating}
              className="mt-3 h-8 rounded px-4 text-xs font-semibold disabled:opacity-60"
              style={{ backgroundColor: primaryColor, color: primaryForeground }}
            >
              {isSubmitting || isValidating
                ? t('emailUnsubscribe.submittingButton')
                : t('emailUnsubscribe.unsubscribeButton')}
            </Button>
          </div>
        )}
      </section>
    </main>
  )
}

export default EmailUnsubscribe
